<script lang="ts">
    
    import { fade } from "svelte/transition";
    import { onDestroy, onMount } from "svelte";
    import type { Room } from "../../Model/Room";
    import { toCanvas } from 'qrcode';
    import WaitingClock from "../../Components/WaitingClock.svelte";
    import type { Game } from "../../Model/Game";
    import { roomsService } from "../../Services";
    import I18n from "../../Components/Shared/I18n.svelte";
    import { CLIENT_URL } from "../../environment";
    import Watch from "../../Components/Watch.svelte";
    import Switcher from "../../Components/Shared/Switcher.svelte";
    import PictureImg from "../../Components/Shared/PictureImg.svelte";
    import AspectRatio from "../../Components/Shared/AspectRatio.svelte";
    import If from "../../Components/Shared/If.svelte";
    import { State } from "../../Model/State";
    import RoomQr from "../../Components/Rooms/RoomQr.svelte";
    import { i18n } from "../../Services/i18n";

    export let room: Room;

    const roomEventsChannel = room.getEventsChannel();
    let chromaEnabled = false;
    let chromaColor = '';

    roomEventsChannel.on('game_start', onGameStart);

    let canvas: HTMLCanvasElement;
    let game: Game;
    let showQr: State<boolean>;

    onMount(async () => {
        roomEventsChannel.on('game_start', onGameStart);
        roomEventsChannel.on('game_cancel', onGameCancel);
        roomEventsChannel.on('updated', onUpdated);
        
        // Suscribirse a los cambios del chroma
        room.chroma.subscribe(value => chromaEnabled = value);
        room.chroma_color.subscribe(value => chromaColor = value);

        try {
            game = await roomsService.getCurrentGameAtRoom(room.id);
        } catch(error) {
            console.log(error);
        }

        const roomUrl = new URL(`/room/${room.id}?gameCode=${game.code}`, CLIENT_URL);

        toCanvas(canvas, roomUrl.href, {
            width: 500,
        });

        console.info(`Game Link: ${roomUrl.href}`)

        if(room.showRoomQrInMonitor.value && !game.playWithPaper) {
            showQr = new State(true);
        } else if(room.showRoomQrInMonitor.value && game.playWithPaper) {
            showQr = new State(false);
        } else if(!room.showRoomQrInMonitor.value && !game.playWithPaper) {
            showQr = new State(false);
        }
    });

    function onUpdated() {
        location.reload();
    }

    onDestroy(() => {
        roomEventsChannel.destroy();
    });

    function onGameStart(): void {
        room.state.set('playing');
    }

    function onGameCancel(): void {
        room.state.set('idle');
    }

    // Aplicar estilos del chroma cuando cambien las propiedades
    $: if (chromaEnabled) {
        const monitorDiv = document.querySelector('.monitor-view') as HTMLElement;
        if (monitorDiv) {
            if (chromaColor && chromaColor.trim() !== '') {
                monitorDiv.style.backgroundColor = chromaColor;
                monitorDiv.style.backgroundImage = 'none';
            } else {
                monitorDiv.style.backgroundColor = '#32ff03';
                monitorDiv.style.backgroundImage = 'none';
            }
        }
    } else {
        const monitorDiv = document.querySelector('.monitor-view') as HTMLElement;
        if (monitorDiv) {
            monitorDiv.style.backgroundColor = '';
            monitorDiv.style.backgroundImage = '';
        }
    }

</script>
    <div class="monitor-view">
        <If state={showQr}>
            <div class="qr-zone">
                <div class="container">
                    <img class="game-logo" src="/images/game-name.png" alt="">
                    <RoomQr roomId={room.id} gameId={game?.id} margin={1} />
                    <!-- <canvas width="500" height="500" bind:this={canvas}></canvas> -->
                    <div class="game-code">
                        <div class="code">{game?.code || '----'}</div>
                    </div>
                </div>
            </div>
        </If>

        <div class="clock-zone notranslate {game && game.playWithPaper ? 'mx-3' : ''}">
            <AspectRatio ratio={9/16}>
                <div class="room-gallery">
                    <Switcher items={[...room.gallery]} let:item={pictureId} time={8000}>
                        {#key pictureId}
                            <div class="picture" transition:fade>
                                <PictureImg id={pictureId} />
                            </div>
                        {/key}
                    </Switcher>
                </div>
            </AspectRatio>
            
            <div class="clock notranslate">
                {#if game}
                    <div class="time">
                        <WaitingClock {game} />
                    </div>
                {/if}
            </div>
            <div class="awards">
                <div class="title">
                    <I18n text="Premios" />
                </div>
                <ul>
                    {#each ['bingo', 'figure'] as rewardName}
                        {#if room.roomRewards[rewardName].text.value}
                            <li class='d-flex align-items-center'>
                                <div class='d-flex flex-column lh-sm'>
                                    {#if rewardName == 'bingo'}
                                        <span class="fw-light fs-6 mt-3 text-uppercase">{rewardName} - full</span>
                                    {:else}
                                        <span class="fw-light fs-6 mt-3 text-uppercase">{$i18n('Línea')} - {$i18n('Figura')}</span>
                                    {/if}
                                    <Watch state={room.roomRewards[rewardName].text} />
                                </div>
                            </li>
                        {/if}
                    {/each}
                </ul>
            </div>
        </div>

    </div>

<style lang="scss">

    .monitor-view {
        display: flex;
        justify-content: center;
        gap: 2rem;
        overflow: hidden;
    }

    .qr-zone {
        flex: 1 1 0;
        display: flex;
        flex-direction: column-reverse;
        align-items: center;
        animation: qr-zone 1s;
    }
    
    .clock-zone {
        flex: 1 1 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        animation: clock-zone 1s;
    }

    .container {
        display: flex;
        flex-direction: column;
        align-items: center;
        height: calc(100% - 10rem);
        width: 90%;
        max-width: 600px;
        background-color: #fff;
        border-top-left-radius: 3rem;
        border-top-right-radius: 3rem;

        canvas {
            max-width: 100%;
        }
    }

    .game-logo {
        width: 70%;
        margin-top: -8%;
    }

    .room-gallery {
        width: 100%;
        height: 100%;
        position: relative;

        .picture {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;

            :global(img) {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }
    }

    .clock {
        font-family: ManagerTitle;
        
        .time {
            font-size: 8rem;
        }
    }

    .game-code {
        margin: 2vw 0;

        .code {
            border: 0.4vw solid #00152E;
            color: #00152E;
            font-size: 5vw;
            padding: 0 2vw;
            border-radius: 1vw;
        }
    }

    .awards {
        width: 100%;

        .title {
            background-color: #00152E;
            padding: 1rem;
            font-family: ManagerTitle;
            font-size: 3rem;
            text-transform: uppercase;
            border-top-left-radius: 1rem;
            border-bottom-left-radius: 1rem;
            margin-bottom: 2rem;
        }

        ul {

            li {
                list-style: none;
                font-size: 2rem;

                &::before {
                    content: '';
                    display: inline-block;
                    width: 0.5em;
                    height: 0.5em;
                    border-radius: 50%;
                    background-color: #00BEEE;
                    margin-right: 0.5em;
                }
            }

        }

    }

    @keyframes qr-zone {
        from {
            transform: translateY(100%);
        }
        to {
            transform: translateY(0);
        }
    }

    @keyframes clock-zone {
        from {
            transform: translateY(-100%);
        }
        to {
            transform: translateY(0);
        }
    }

</style>
